import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, Search, FileJson, Layers } from "lucide-react";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Standards page — Sprint 6 International Standards.
 *
 * Two things:
 *  1. Live search across the curated ICD-10 / LOINC / RxNorm dictionaries
 *  2. One-click FHIR R4 Bundle export for the scoped patient
 */
export default function Standards() {
  const [system, setSystem] = useState("icd10");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/standards/catalog").then((r) => setCatalog(r.data.catalog)).catch(() => {});
  }, []);

  const search = useCallback(async (sys, query) => {
    setSearching(true);
    try {
      const { data } = await api.get(
        `/standards/${sys}/search?q=${encodeURIComponent(query || "")}&limit=25`
      );
      setResults(data.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => { search(system, q); }, [search, system, q]);

  async function downloadFhir() {
    setExporting(true);
    try {
      const { data } = await api.get("/patients/fhir/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/fhir+json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fhir-bundle-${data.id}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`FHIR Bundle exported (${data.entry?.length || 0} resources)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "FHIR export failed");
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="standards-page">
      <div className="ros-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(96,165,250,0.14)", border: "1px solid rgba(96,165,250,0.5)" }}>
            <Layers size={22} style={{ color: "#60A5FA" }} />
          </div>
          <div>
            <h2 className="font-heading text-2xl">International Standards</h2>
            <p className="text-sm text-[#A3ACA7] mt-0.5">
              ICD-10-CM · LOINC · RxNorm — every clinical concept in Med Nexus is FHIR-portable.
            </p>
          </div>
        </div>
        {catalog && (
          <div className="mt-4 grid grid-cols-3 gap-3" data-testid="standards-catalog">
            <StatCard label="ICD-10-CM" value={catalog.icd10}  desc="diagnoses"     accent="#F87171" />
            <StatCard label="LOINC"     value={catalog.loinc}  desc="observations"  accent="#4ADE80" />
            <StatCard label="RxNorm"    value={catalog.rxnorm} desc="medications"   accent="#A78BFA" />
          </div>
        )}
      </div>

      {/* FHIR Export CTA */}
      <button
        type="button"
        onClick={downloadFhir}
        disabled={exporting}
        data-testid="fhir-export-btn"
        className="w-full text-left ros-card hover:border-[#60A5FA]/60 transition-colors disabled:opacity-50"
        style={{ background: "rgba(96,165,250,0.10)", borderColor: "rgba(96,165,250,0.45)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
                 style={{ background: "rgba(96,165,250,0.24)", border: "1px solid rgba(96,165,250,0.55)" }}>
              {exporting
                ? <Loader2 size={18} className="animate-spin" style={{ color: "#60A5FA" }} />
                : <Download size={18} style={{ color: "#60A5FA" }} />}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#60A5FA" }}>
                Export FHIR R4 Bundle
              </div>
              <div className="font-heading text-base text-white mt-0.5">
                Download your record as a portable, standards-compliant JSON bundle
              </div>
              <div className="text-xs text-[#A3ACA7] mt-1 max-w-2xl">
                Any Epic, Cerner, HAPI FHIR server, or CommonWell node can ingest this
                bundle via a standard <span className="font-mono">POST /fhir/Bundle</span>. Includes
                every specialty: nephro · cardio · neuro · onco.
              </div>
            </div>
          </div>
          <FileJson size={16} style={{ color: "#60A5FA" }} />
        </div>
      </button>

      {/* Code search */}
      <div className="ros-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-heading text-lg">Code lookup</div>
          <div className="flex gap-2" data-testid="system-tabs">
            {[
              { key: "icd10",  label: "ICD-10" },
              { key: "loinc",  label: "LOINC"  },
              { key: "rxnorm", label: "RxNorm" },
            ].map((s) => {
              const on = system === s.key;
              return (
                <button key={s.key} onClick={() => setSystem(s.key)}
                        data-testid={`system-tab-${s.key}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          on ? "bg-[#60A5FA] text-black border-transparent"
                             : "text-[#A3ACA7] border-[#272E2B] hover:text-white"
                        }`}>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 border border-[#272E2B] rounded-full px-3 py-2 bg-[#0F1312]">
          <Search size={14} className="text-[#A3ACA7]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="standards-search-input"
            placeholder={`Search ${system.toUpperCase()}… (e.g. "chronic kidney", "cholesterol", "trastuzumab")`}
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-[#57534E]"
          />
          {searching && <Loader2 size={14} className="animate-spin text-[#A3ACA7]" />}
        </div>

        <div className="mt-4 overflow-x-auto" data-testid="standards-results">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-[#A3ACA7] uppercase tracking-wider">
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">
                  {system === "rxnorm" ? "Drug" : "Display"}
                </th>
                <th className="py-2 pr-4">
                  {system === "loinc" ? "Unit" : system === "rxnorm" ? "Class" : "Specialty"}
                </th>
                {system === "loinc" && <th className="py-2">Specialty</th>}
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr><td colSpan={4} className="py-4 text-[#A3ACA7]">No results.</td></tr>
              ) : results.map((r) => (
                <tr key={(r.code || r.cui) + (r.name || r.display)}
                    className="border-t border-[#272E2B]"
                    data-testid={`standards-row-${r.code || r.cui}`}>
                  <td className="py-2 pr-4 font-mono text-white">{r.code || r.cui}</td>
                  <td className="py-2 pr-4 text-white">{r.display || r.name}</td>
                  <td className="py-2 pr-4 text-[#A3ACA7] text-xs">
                    {system === "loinc" ? (r.unit || "—")
                      : system === "rxnorm" ? r.class
                      : r.specialty}
                  </td>
                  {system === "loinc" && <td className="py-2 text-xs text-[#A3ACA7]">{r.specialty}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, desc, accent }) {
  return (
    <div className="ros-card-raised" style={{ borderColor: `${accent}40` }}>
      <div className="text-[10px] uppercase tracking-wider" style={{ color: accent }}>{label}</div>
      <div className="text-3xl font-heading font-bold text-white mt-1">{value}</div>
      <div className="text-[10px] text-[#A3ACA7]">{desc}</div>
    </div>
  );
}
